// Controls reachable before and after login: language, appearance, and a
// copyable one-time secret. The copy action never logs or persists the value.

import { useState, type ReactNode } from 'react';
import {
  ActionIcon,
  Button,
  CopyButton,
  Group,
  SegmentedControl,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { useI18n } from '@/i18n/I18nProvider';
import { useAppearance } from '@/theme/AppearanceProvider';

/** Language selector, usable while signed out. */
export function LanguageControl(): ReactNode {
  const { language, setLanguage, t } = useI18n();
  return (
    <SegmentedControl
      size="xs"
      value={language}
      onChange={(value) => setLanguage(value)}
      aria-label={t('language.label')}
      data={[
        { value: 'en', label: t('language.en') },
        { value: 'hr', label: t('language.hr') },
      ]}
    />
  );
}

/** Appearance selector following system by default. */
export function AppearanceControl(): ReactNode {
  const { preference, setPreference } = useAppearance();
  const { t } = useI18n();
  return (
    <SegmentedControl
      size="xs"
      value={preference}
      onChange={(value) => setPreference(value)}
      aria-label={t('theme.label')}
      data={[
        { value: 'light', label: t('theme.light') },
        { value: 'dark', label: t('theme.black') },
        { value: 'auto', label: t('theme.system') },
      ]}
    />
  );
}

/**
 * A one-time secret (pairing code or intake token) with a copy action.
 * The value lives in component state only: it is never written to storage,
 * placed in a URL, or included in logs.
 */
export function CopyableSecret({
  value,
  label,
  copyLabel,
  monospace = true,
}: {
  value: string;
  label?: string;
  copyLabel?: string;
  monospace?: boolean;
}): ReactNode {
  const { t } = useI18n();
  const [announcement, setAnnouncement] = useState('');

  return (
    <Group gap="sm" align="center" wrap="wrap">
      {label ? (
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      ) : null}
      <Text
        component="code"
        className={monospace ? 'sensing-numeric' : undefined}
        fw={700}
        fz="lg"
        p="xs"
        style={{
          letterSpacing: '0.08em',
          background: 'var(--sensing-surface-sunken)',
          border: '2px dashed var(--sensing-border-strong)',
          borderRadius: 10,
          userSelect: 'all',
        }}
      >
        {value}
      </Text>
      <CopyButton value={value} timeout={2000}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? t('common.copied') : (copyLabel ?? t('common.copy'))} withArrow>
            <Button
              variant="light"
              size="xs"
              leftSection={
                copied ? <IconCheck size={14} aria-hidden /> : <IconCopy size={14} aria-hidden />
              }
              onClick={() => {
                copy();
                setAnnouncement(t('common.copied'));
              }}
            >
              {copyLabel ?? t('common.copy')}
            </Button>
          </Tooltip>
        )}
      </CopyButton>
      <Text size="xs" c="transparent" aria-live="polite" style={{ position: 'absolute' }}>
        {announcement}
      </Text>
    </Group>
  );
}

/** A compact icon-only copy button for inline row actions. */
export function InlineCopy({ value, label }: { value: string; label: string }): ReactNode {
  const { t } = useI18n();
  return (
    <CopyButton value={value} timeout={2000}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? t('common.copied') : label} withArrow>
          <ActionIcon variant="subtle" size="sm" aria-label={label} onClick={copy}>
            {copied ? <IconCheck size={14} aria-hidden /> : <IconCopy size={14} aria-hidden />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}
